import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, RotateCw } from "lucide-react";
import { formatClock } from "@/lib/format";

const SPEEDS = [1, 1.5, 2];

// Custom touch-friendly player. Streams through our backend (Range support for
// iPhone Safari). `duration` (seconds, computed backend-side) shows the total
// before the browser loads metadata; once loadedmetadata fires we prefer the
// browser's own value.
export const AudioPlayer = ({ src, duration, disabled = false, seekSignal = null }) => {
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [metaDuration, setMetaDuration] = useState(null);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [dragging, setDragging] = useState(false);

  const total = metaDuration && isFinite(metaDuration) && metaDuration > 0 ? metaDuration : duration || 0;
  const pct = total > 0 ? Math.min(100, (time / total) * 100) : 0;

  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setMetaDuration(null);
    setSpeedIdx(0);
  }, [src]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIdx];
  }, [speedIdx, src]);

  // External seek (tapping a transcript timestamp). A tap is a user gesture,
  // so starting playback right after the seek is allowed.
  useEffect(() => {
    if (!seekSignal) return;
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = seekSignal.t;
    setTime(seekSignal.t);
    a.play().catch(() => {});
  }, [seekSignal]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      try {
        await a.play();
      } catch {
        /* lecture impossible (fichier manquant, restrictions navigateur) */
      }
    }
  };

  const seekToClientX = (clientX) => {
    const bar = barRef.current;
    if (!bar || !total) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = ratio * total;
    setTime(t);
    if (audioRef.current) audioRef.current.currentTime = t;
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    seekToClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging) seekToClientX(e.clientX);
  };
  const endDrag = () => setDragging(false);

  const skip = (delta) => {
    const a = audioRef.current;
    const t = Math.min(Math.max((a ? a.currentTime : time) + delta, 0), total || Infinity);
    setTime(t);
    if (a) a.currentTime = t;
  };

  if (disabled || !src) {
    return (
      <div data-testid="audio-player" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 min-h-[120px] flex items-center opacity-60">
        <div className="flex items-center gap-[3px] h-8 w-full">
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} className="w-[3px] rounded-full bg-slate-300" style={{ height: "15%" }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audio-player" className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 min-h-[120px] flex items-center">
      <div className="flex items-center gap-4 w-full">
        <button
          type="button"
          data-testid="audio-play-toggle"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Lecture"}
          className="h-14 w-14 shrink-0 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md shadow-emerald-600/25 hover:bg-emerald-700 active:scale-95 transition-all"
        >
          {playing ? <Pause className="h-6 w-6 fill-current" /> : <Play className="h-6 w-6 fill-current ml-0.5" />}
        </button>

        <div className="flex-1 min-w-0">
          <div
            ref={barRef}
            data-testid="audio-progress"
            role="slider"
            aria-label="Progression"
            aria-valuemin={0}
            aria-valuemax={Math.round(total)}
            aria-valuenow={Math.round(time)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="relative h-8 flex items-center cursor-pointer touch-none select-none"
          >
            <div className="h-1.5 w-full rounded-full bg-emerald-100">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
            </div>
            <div
              data-testid="audio-thumb"
              className="absolute h-5 w-5 rounded-full bg-white border-2 border-emerald-600 shadow-sm pointer-events-none"
              style={{ left: `calc(${pct}% - 10px)` }}
            />
          </div>

          <div className="mt-1 flex items-center justify-between gap-2">
            <span data-testid="audio-time" className="text-xs font-medium text-slate-600 tabular-nums whitespace-nowrap shrink-0">
              {formatClock(time)} / {formatClock(total)}
            </span>
            {/* Full buttons (≥391px) */}
            <div className="flex items-center gap-1.5 max-[391px]:hidden">
              <button
                type="button"
                data-testid="audio-skip-back"
                onClick={() => skip(-10)}
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors whitespace-nowrap"
              >
                −10 s
              </button>
              <button
                type="button"
                data-testid="audio-skip-forward"
                onClick={() => skip(10)}
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors whitespace-nowrap"
              >
                +10 s
              </button>
              <button
                type="button"
                data-testid="audio-speed-toggle"
                onClick={() => setSpeedIdx((speedIdx + 1) % SPEEDS.length)}
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors min-w-[42px] tabular-nums whitespace-nowrap"
              >
                {SPEEDS[speedIdx]}×
              </button>
            </div>
            {/* Compact icon buttons (≤390px): rewind/forward with "10" inside */}
            <div className="hidden max-[391px]:flex items-center gap-1 shrink-0">
              <button
                type="button"
                data-testid="audio-skip-back"
                onClick={() => skip(-10)}
                aria-label="Reculer de 10 secondes"
                className="h-8 w-8 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors flex items-center justify-center"
              >
                <span className="relative flex items-center justify-center">
                  <RotateCcw className="h-4 w-4" />
                  <span className="absolute text-[7px] font-bold leading-none">10</span>
                </span>
              </button>
              <button
                type="button"
                data-testid="audio-skip-forward"
                onClick={() => skip(10)}
                aria-label="Avancer de 10 secondes"
                className="h-8 w-8 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors flex items-center justify-center"
              >
                <span className="relative flex items-center justify-center">
                  <RotateCw className="h-4 w-4" />
                  <span className="absolute text-[7px] font-bold leading-none">10</span>
                </span>
              </button>
              <button
                type="button"
                data-testid="audio-speed-toggle"
                onClick={() => setSpeedIdx((speedIdx + 1) % SPEEDS.length)}
                aria-label="Vitesse de lecture"
                className="h-8 min-w-[34px] px-1.5 rounded-full border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors tabular-nums whitespace-nowrap"
              >
                {SPEEDS[speedIdx]}×
              </button>
            </div>
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        data-testid="audio-element"
        src={src}
        preload="none"
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          if (!dragging) setTime(e.currentTarget.currentTime);
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d) && d > 0) setMetaDuration(d);
        }}
      />
    </div>
  );
};
